import { TypeManagerDecorator } from "../../../main/type.map.service";
import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { UserStore } from "../../../stores/user.store";
import { activityLabel, ENGAGEMENT_LABELS } from "./engagement.labels";
import { EngagementBadge, EngagementBadgeEarned, EngagementService } from "./engagement.service";
import { S25FilestoreImageComponent } from "../../../standalone/s25.filestore.image.component";
import { SeBadgeShowcaseComponent } from "./se.badge.showcase.component";

@TypeManagerDecorator("s25-ng-se-my-activity")
@Component({
    selector: "s25-ng-se-my-activity",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [CommonModule, SeBadgeShowcaseComponent, S25FilestoreImageComponent],
    template: `
        <div class="se-my-activity">
            <h2 class="se-my-activity__title">{{ labels.myActivityTitle }}</h2>

            <section class="se-my-activity__section" aria-labelledby="se-my-activity-badges">
                <button
                    class="se-my-activity__collapsible-toggle"
                    type="button"
                    [attr.aria-expanded]="badgesExpanded()"
                    aria-controls="se-my-activity-badges-content"
                    (click)="badgesExpanded.set(!badgesExpanded())"
                >
                    <h3 id="se-my-activity-badges" class="se-my-activity__heading">Earned badges</h3>
                    <span
                        class="se-my-activity__toggle-icon"
                        [class.se-my-activity__toggle-icon--open]="badgesExpanded()"
                        >&#9654;</span
                    >
                </button>
                @if (badgesExpanded()) {
                    <div id="se-my-activity-badges-content">
                        @if (myBadges.resource.isLoading() && !myBadges.lastValue()) {
                            <p class="se-my-activity__muted">Loading badges…</p>
                        } @else if (myBadges.resource.error()) {
                            <p class="se-my-activity__error" role="alert">{{ myBadges.resource.error()!.message }}</p>
                        } @else {
                            <div class="se-my-activity__badge-grid">
                                @for (row of earnedWithBadge(); track row.earned.earnedId) {
                                    <div class="se-my-activity__badge-card">
                                        @if (row.badge?.iconUrl) {
                                            <s25-ng-filestore-image
                                                class="se-my-activity__badge-icon"
                                                [value]="row.badge.iconUrl"
                                                [readOnly]="true"
                                            />
                                        } @else {
                                            <div
                                                class="se-my-activity__badge-icon se-my-activity__badge-icon--placeholder"
                                                aria-hidden="true"
                                            >
                                                ★
                                            </div>
                                        }
                                        <div class="se-my-activity__badge-text">
                                            <div class="se-my-activity__badge-name">{{ row.displayName }}</div>
                                            @if (row.badge?.description?.trim()) {
                                                <p class="se-my-activity__badge-desc">{{ row.badge?.description }}</p>
                                            }
                                            <time
                                                class="se-my-activity__badge-date"
                                                [attr.datetime]="row.earned.earnedDt"
                                                >{{ formatDate(row.earned.earnedDt) }}</time
                                            >
                                        </div>
                                    </div>
                                } @empty {
                                    <p class="se-my-activity__muted">No badges earned yet.</p>
                                }
                            </div>
                        }
                    </div>
                }
            </section>

            <section class="se-my-activity__section" aria-labelledby="se-my-activity-showcase">
                <button
                    class="se-my-activity__collapsible-toggle"
                    type="button"
                    [attr.aria-expanded]="showcaseExpanded()"
                    aria-controls="se-my-activity-showcase-content"
                    (click)="showcaseExpanded.set(!showcaseExpanded())"
                >
                    <h3 id="se-my-activity-showcase" class="se-my-activity__heading">
                        {{ labels.badgeShowcaseTitle }}
                    </h3>
                    <span
                        class="se-my-activity__toggle-icon"
                        [class.se-my-activity__toggle-icon--open]="showcaseExpanded()"
                        >&#9654;</span
                    >
                </button>
                @if (showcaseExpanded()) {
                    <div id="se-my-activity-showcase-content">
                        <s25-ng-se-badge-showcase [hideTitle]="true" />
                    </div>
                }
            </section>

            <section class="se-my-activity__section" aria-labelledby="se-my-activity-history">
                <button
                    class="se-my-activity__collapsible-toggle"
                    type="button"
                    [attr.aria-expanded]="activityExpanded()"
                    aria-controls="se-my-activity-history-content"
                    (click)="activityExpanded.set(!activityExpanded())"
                >
                    <h3 id="se-my-activity-history" class="se-my-activity__heading">Activity</h3>
                    <span
                        class="se-my-activity__toggle-icon"
                        [class.se-my-activity__toggle-icon--open]="activityExpanded()"
                        >&#9654;</span
                    >
                </button>
                @if (activityExpanded()) {
                    <div id="se-my-activity-history-content">
                        @if (history.resource.isLoading() && !history.lastValue()) {
                            <p class="se-my-activity__muted">Loading activity…</p>
                        } @else if (history.resource.error()) {
                            <p class="se-my-activity__error" role="alert">{{ history.resource.error()!.message }}</p>
                        } @else {
                            <ul class="se-my-activity__list">
                                @for (a of sortedHistory(); track a.activityId) {
                                    <li class="se-my-activity__item">
                                        <span class="se-my-activity__type">{{ activityLabel(a.activityTypeKey) }}</span>
                                        <time class="se-my-activity__date" [attr.datetime]="a.activityDt">{{
                                            formatDate(a.activityDt)
                                        }}</time>
                                    </li>
                                } @empty {
                                    <li class="se-my-activity__muted">No recent activity.</li>
                                }
                            </ul>
                        }
                    </div>
                }
            </section>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .se-my-activity__title {
                margin: 0 0 1.25rem;
            }
            .se-my-activity__section {
                margin-bottom: 2rem;
            }
            .se-my-activity__heading {
                margin: 0 0 0.75rem;
                font-size: 1.1rem;
            }
            .se-my-activity__list {
                list-style: none;
                margin: 0;
                padding: 0;
            }
            .se-my-activity__item {
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                gap: 0.35rem 1rem;
                padding: 0.5rem 0;
                border-bottom: 1px solid var(--color--border);
            }
            .se-my-activity__type {
                font-weight: 600;
            }
            .se-my-activity__date {
                color: var(--color--text-muted);
                font-size: 0.9rem;
            }
            .se-my-activity__badge-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
                gap: 1rem;
            }
            .se-my-activity__badge-card {
                display: flex;
                align-items: flex-start;
                gap: 0.75rem;
                padding: 0.75rem;
                border: 1px solid var(--color--border);
                border-radius: 8px;
                background: var(--color--surface-2-bg);
            }
            .se-my-activity__badge-icon {
                flex-shrink: 0;
                width: 48px;
                height: 48px;
                overflow: hidden;
            }
            .se-my-activity__badge-icon img {
                width: 48px;
                height: 48px;
                object-fit: contain;
                border-radius: 4px;
                border: none;
            }
            .se-my-activity__badge-icon--placeholder {
                width: 48px;
                height: 48px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: linear-gradient(135deg, var(--cn-navy) 0%, var(--cn-navy-700) 100%);
                color: var(--color--surface-bg);
                font-size: 1.5rem;
                border-radius: 8px;
            }
            .se-my-activity__badge-name {
                font-weight: 600;
            }
            .se-my-activity__badge-desc {
                margin: 0.2rem 0 0;
                font-size: 0.82rem;
                color: var(--color--text-muted);
                line-height: 1.4;
            }
            .se-my-activity__badge-date {
                font-size: 0.8rem;
                color: var(--color--text-muted);
            }
            .se-my-activity__muted {
                color: var(--color--text-muted);
                margin: 0;
            }
            .se-my-activity__error {
                color: var(--color--danger);
                margin: 0;
            }
            .se-my-activity__collapsible-toggle {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                width: 100%;
                padding: 0;
                border: none;
                background: none;
                cursor: pointer;
                text-align: left;
            }
            .se-my-activity__collapsible-toggle .se-my-activity__heading {
                margin: 0;
            }
            .se-my-activity__toggle-icon {
                font-size: 0.75rem;
                transition: transform var(--transition-medium) ease;
                color: var(--color--text-muted);
            }
            .se-my-activity__toggle-icon--open {
                transform: rotate(90deg);
            }
        `,
    ],
})
export class SeMyActivityComponent {
    readonly labels = ENGAGEMENT_LABELS;
    readonly badgesExpanded = signal(true);
    readonly showcaseExpanded = signal(true);
    readonly activityExpanded = signal(false);
    private readonly userStore = inject(UserStore);

    readonly history = s25Resource(() => EngagementService.getMyHistory());
    readonly myBadges = s25Resource(() => EngagementService.getMyBadges());
    readonly badgeCatalog = s25Resource(() => EngagementService.getBadges());

    readonly badgeById = computed((): Map<number, EngagementBadge> => {
        const list = this.badgeCatalog.value() ?? [];
        return new Map(list.filter((b) => b.badgeId != null).map((b) => [b.badgeId!, b]));
    });

    readonly sortedHistory = computed(() => {
        const rows = this.history.value() ?? [];
        return [...rows].sort((a, b) => (a.activityDt < b.activityDt ? 1 : a.activityDt > b.activityDt ? -1 : 0));
    });

    readonly earnedWithBadge = computed(
        (): { earned: EngagementBadgeEarned; badge?: EngagementBadge; displayName: string }[] => {
            const earned = this.myBadges.value() ?? [];
            const byId = this.badgeById();
            return earned.map((e) => {
                const badge = byId.get(e.badgeId);
                return {
                    earned: e,
                    badge,
                    displayName: badge?.name ?? String(e.badgeId),
                };
            });
        },
    );

    readonly activityLabel = activityLabel;

    formatDate(iso: string): string {
        const fmt = this.userStore.dateTimeFormat();
        if (!fmt) return iso;
        return S25Datefilter.transform(S25Util.date.parse(iso), fmt) || iso;
    }
}
